import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Briefcase, MapPin, Clock, Upload, FileText, X } from "lucide-react";
import { toast } from "sonner";
import { SectionHeading } from "../components/shared/SectionHeading";
import CTABanner from "../components/shared/CTABanner";
import { JOBS } from "../lib/data";
import { applyCareer } from "../lib/api";

const emptyForm = {
    full_name: "",
    email: "",
    phone: "",
    role: "",
    experience_years: "",
    linkedin: "",
    message: "",
};

export default function Careers() {
    const [form, setForm] = useState(emptyForm);
    const [resume, setResume] = useState(null);
    const [loading, setLoading] = useState(false);
    const fileInputRef = useRef(null);

    const pickRole = (title) => {
        setForm((f) => ({ ...f, role: title }));
        document
            .getElementById("apply-form")
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const onChange = (e) =>
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onPickFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const ok =
            /\.(pdf|doc|docx)$/i.test(f.name) ||
            [
                "application/pdf",
                "application/msword",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            ].includes(f.type);
        if (!ok) {
            toast.error("Only PDF or DOC/DOCX files are allowed.");
            e.target.value = "";
            return;
        }
        if (f.size > 10 * 1024 * 1024) {
            toast.error("File exceeds 10 MB limit.");
            e.target.value = "";
            return;
        }
        setResume(f);
    };

    const clearResume = () => {
        setResume(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.full_name || !form.email || !form.role) {
            toast.error("Please fill name, email and role.");
            return;
        }
        setLoading(true);
        try {
            await applyCareer(form, resume);
            toast.success("Application received.", {
                description: resume
                    ? "Our talent team will review your resume and be in touch soon."
                    : "Our talent team will be in touch soon.",
            });
            setForm(emptyForm);
            clearResume();
        } catch (err) {
            toast.error("Couldn't submit.", {
                description:
                    err?.response?.data?.detail || "Please try again in a moment.",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <main data-testid="page-careers" className="relative pt-32 md:pt-40">
            <section className="relative pb-16">
                <div className="absolute inset-0 -z-10 bg-radial-fade" />
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <SectionHeading
                        eyebrow="Careers"
                        title="Build the future with senior peers."
                        subtitle="We hire practitioners who care about craft. Remote-friendly, outcome-focused and globally distributed."
                    />
                </div>
            </section>

            <section className="relative py-14" data-testid="careers-openings-section">
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {JOBS.map((j, i) => (
                        <motion.button
                            key={j.id}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.55, delay: i * 0.05 }}
                            onClick={() => pickRole(j.title)}
                            className="group glass rounded-3xl border border-white/5 p-7 text-left brand-border-hover tracing-beam"
                            data-testid={`job-card-${j.id}`}
                            type="button"
                        >
                            <div className="flex items-center justify-between">
                                <span className="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-brand-green">
                                    {j.team}
                                </span>
                                <ArrowUpRight className="h-4 w-4 text-zinc-600 group-hover:text-brand-green transition-colors" />
                            </div>
                            <h3 className="mt-6 font-display text-xl md:text-2xl font-semibold tracking-tight leading-tight">
                                {j.title}
                            </h3>
                            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-zinc-400">
                                <span className="inline-flex items-center gap-1.5">
                                    <MapPin className="h-3.5 w-3.5" />
                                    {j.location}
                                </span>
                                <span className="inline-flex items-center gap-1.5">
                                    <Briefcase className="h-3.5 w-3.5" />
                                    {j.type}
                                </span>
                                {j.experience && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <Clock className="h-3.5 w-3.5" />
                                        {j.experience}
                                    </span>
                                )}
                            </div>
                            <div className="mt-6 text-[13px] text-brand-green group-hover:text-white transition">
                                Apply for this role →
                            </div>
                        </motion.button>
                    ))}
                </div>
            </section>

            <section
                id="apply-form"
                data-testid="careers-apply-section"
                className="relative py-16 md:py-20 border-t border-white/5"
            >
                <div className="max-w-[1100px] mx-auto px-5 md:px-8 lg:px-10">
                    <SectionHeading
                        eyebrow="Apply now"
                        title="Tell us about you."
                        subtitle="One quick form we read every application carefully."
                    />

                    <form
                        onSubmit={onSubmit}
                        data-testid="career-apply-form"
                        encType="multipart/form-data"
                        className="mt-12 glass rounded-3xl border border-white/5 p-8 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5"
                    >
                        <FormField label="Full name" name="full_name" value={form.full_name} onChange={onChange} required />
                        <FormField label="Email" name="email" type="email" value={form.email} onChange={onChange} required />
                        <FormField label="Phone" name="phone" value={form.phone} onChange={onChange} />
                        <FormField label="Role" name="role" value={form.role} onChange={onChange} required />
                        <FormField label="Years of experience" name="experience_years" value={form.experience_years} onChange={onChange} />
                        <FormField label="LinkedIn URL" name="linkedin" value={form.linkedin} onChange={onChange} />
                        <div className="md:col-span-2">
                            <label className="text-[12px] uppercase tracking-[0.2em] text-zinc-500">Why Netfotech?</label>
                            <textarea
                                name="message"
                                rows={4}
                                value={form.message}
                                onChange={onChange}
                                data-testid="career-field-message"
                                className="mt-2 w-full rounded-xl border border-white/10 bg-ink-800/80 px-4 py-3 text-[14.5px] text-white placeholder:text-zinc-500 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none resize-none"
                                placeholder="Tell us what you're looking for next."
                            />
                        </div>

                        {/* RESUME UPLOAD */}
                        <div className="md:col-span-2">
                            <label className="text-[12px] uppercase tracking-[0.2em] text-zinc-500">
                                Resume (PDF or DOC/DOCX, max 10 MB)
                            </label>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                onChange={onPickFile}
                                data-testid="career-field-resume"
                                className="hidden"
                                id="career-resume-input"
                            />
                            {!resume ? (
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    data-testid="career-resume-button"
                                    className="mt-2 w-full flex items-center justify-center gap-3 rounded-xl border border-dashed border-white/15 bg-white/[0.02] px-5 py-8 text-[14px] text-zinc-300 hover:border-brand-blue/50 hover:bg-white/[0.04] transition-all"
                                >
                                    <Upload className="h-5 w-5 text-brand-blue" />
                                    <span>Click to upload your resume</span>
                                    <span className="text-zinc-500 text-[12px]">· PDF or DOC/DOCX</span>
                                </button>
                            ) : (
                                <div
                                    data-testid="career-resume-attached"
                                    className="mt-2 flex items-center justify-between gap-3 rounded-xl border border-brand-green/30 bg-brand-green/5 px-4 py-3.5"
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <FileText className="h-4 w-4 text-brand-green shrink-0" />
                                        <div className="min-w-0">
                                            <div className="text-[13.5px] text-white truncate">{resume.name}</div>
                                            <div className="text-[11px] text-zinc-500">
                                                {(resume.size / 1024).toFixed(1)} KB
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={clearResume}
                                        data-testid="career-resume-remove"
                                        aria-label="Remove resume"
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-full glass border border-white/10 text-zinc-300 hover:text-white transition"
                                    >
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="md:col-span-2 flex items-center justify-end pt-2">
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="career-submit-button"
                                className="btn-glow relative inline-flex items-center gap-2 rounded-full bg-brand-linear px-7 py-3.5 text-[15px] font-medium text-white hover:shadow-glow-brand transition-all disabled:opacity-60"
                            >
                                {loading ? "Submitting…" : "Submit application"}
                                <ArrowUpRight className="h-4 w-4" />
                            </button>
                        </div>
                    </form>
                </div>
            </section>

            <section className="relative py-16 md:py-20">
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <CTABanner
                        eyebrow="Don't see your role?"
                        title="We're always meeting great people."
                        subtitle="Drop us a note we keep a curated talent pool across practices."
                        ctaLabel="Get in touch"
                        ctaTo="/contact"
                    />
                </div>
            </section>
        </main>
    );
}

function FormField({ label, name, value, onChange, type = "text", required }) {
    return (
        <div>
            <label className="text-[12px] uppercase tracking-[0.2em] text-zinc-500">
                {label}{required && <span className="text-brand-blue"> *</span>}
            </label>
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                required={required}
                data-testid={`career-field-${name}`}
                className="mt-2 w-full rounded-xl border border-white/10 bg-ink-800/80 px-4 py-3 text-[14.5px] text-white placeholder:text-zinc-500 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none"
                placeholder={label}
            />
        </div>
    );
}
